import React, { useMemo, useCallback, useEffect } from 'react';
import { Form, Checkbox, Slider } from 'antd';

const Filter = ({ data, skillsGroup, toShow, resetFlag }) => {
  const [form] = Form.useForm();
  
  const safeData = useMemo(() => (Array.isArray(data) ? data : []), [data]);
  
  useEffect(() => {
    form.resetFields(['organization', 'role_of_the_ecsf', 'domain', 'iso_17024', 'duration', 'language', 'skills_group', 'type_format']);
    toShow(safeData);
  }, [resetFlag, form, safeData, toShow]);

  

  const allOptions = useMemo(() => {
    const buckets = {
      domain: [],
      role_of_the_ecsf: [],
      duration: [],
      iso_17024: [],
      language: [],
      organization: [],
      type_format: [],
      skills_group: [],
      price: [],
    };
    safeData.forEach((item) => {
      const push = (key, val) => {
        if (val == null) return;
        const arr = buckets[key];
        const add = (v) => v != null && !arr.includes(v) && arr.push(v);
        if (Array.isArray(val)) val.forEach(add);
        else add(val);
      };
      push('domain', item.domain);
      push('role_of_the_ecsf', item.role_of_the_ecsf);
      push('duration', item.duration);
      push('iso_17024', item.iso_17024);
      push('language', item.language);
      push('organization', item.organization);
      push('type_format', item.type_format);
      push('skills_group', item.skills_group);
      const p = parseFloat(item.price);
      if (!isNaN(p) && !buckets.price.includes(p)) buckets.price.push(p);
    });
    const sortArr = (arr) => arr.filter((v) => v != null).sort((a, b) => (typeof a === 'number' ? a - b : String(a).localeCompare(String(b))));
    return Object.fromEntries(Object.entries(buckets).map(([k, v]) => [k, sortArr(v)]));
  }, [safeData]);

  const maxPrice = useMemo(() => Math.max(...allOptions.price, 0), [allOptions.price]);

  const handleValuesChange = useCallback(
    (_, values) => {
      let filtered = safeData;
      if (values.organization?.length) filtered = filtered.filter((i) => values.organization.includes(i.organization));
      if (values.role_of_the_ecsf?.length) filtered = filtered.filter((i) => values.role_of_the_ecsf.every((v) => i.role_of_the_ecsf?.includes(v)));
      if (values.domain?.length) filtered = filtered.filter((i) => values.domain.every((v) => i.domain?.includes(v)));
      if (values.iso_17024?.length) filtered = filtered.filter((i) => values.iso_17024.includes(i.iso_17024));
      if (values.duration?.length) filtered = filtered.filter((i) => values.duration.includes(i.duration));
      if (values.language?.length) filtered = filtered.filter((i) => values.language.some((v) => i.language?.includes(v)));
      if (values.skills_group?.length) filtered = filtered.filter((i) => values.skills_group.every((v) => i.skills_group?.includes(v)));
      if (values.type_format?.length) filtered = filtered.filter((i) => values.type_format.includes(i.type_format));
      const priceVal = values.price ?? maxPrice;
      filtered = filtered.filter((i) => (parseFloat(i.price) || 0) <= priceVal);
      toShow(filtered);
    },
    [safeData, toShow, maxPrice]
  );

  const makeOptions = (list, labelFn) => (list || []).map((v) => ({ label: labelFn ? labelFn(v) : v || 'Undefined', value: v }));

  const orgOptions = useMemo(() => makeOptions(allOptions.organization), [allOptions.organization]);
  const ecOptions = useMemo(() => makeOptions(allOptions.role_of_the_ecsf, (id) => skillsGroup.find((g) => g.id === id)?.name || id), [allOptions.role_of_the_ecsf, skillsGroup]);
  const domOptions = useMemo(() => makeOptions(allOptions.domain), [allOptions.domain]);
  const isoOptions = useMemo(() => makeOptions(allOptions.iso_17024, (v) => (v === 1 ? 'Yes' : 'No')), [allOptions.iso_17024]);
  const durOptions = useMemo(() => makeOptions(allOptions.duration, (v) => String(v)), [allOptions.duration]);
  const langOptions = useMemo(() => makeOptions(allOptions.language), [allOptions.language]);
  const skillOptions = useMemo(() => makeOptions(allOptions.skills_group, (id) => skillsGroup.find((g) => g.id === id)?.name || id), [allOptions.skills_group, skillsGroup]);
  const typeOptions = useMemo(() => makeOptions(allOptions.type_format), [allOptions.type_format]);

  return (
    <Form form={form} initialValues={{ price: maxPrice }} layout="vertical" onValuesChange={handleValuesChange} className="filter-form">
      <Form.Item name="organization" label="Organization">
        <Checkbox.Group options={orgOptions} className="filter-checkbox-group" />
      </Form.Item>

      <Form.Item name="role_of_the_ecsf" label="e-Competences (e-CF)">
        <Checkbox.Group options={ecOptions} className="filter-checkbox-group" />
      </Form.Item>

      <Form.Item name="domain" label="Domain">
        <Checkbox.Group options={domOptions} className="filter-checkbox-group" />
      </Form.Item>

      <Form.Item name="iso_17024" label="ISO 17024">
        <Checkbox.Group options={isoOptions} className="filter-checkbox-group" />
      </Form.Item>

      <Form.Item name="duration" label="Duration (hours)">
        <Checkbox.Group options={durOptions} className="filter-checkbox-group" />
      </Form.Item>

      <Form.Item name="language" label="Language">
        <Checkbox.Group options={langOptions} className="filter-checkbox-group" />
      </Form.Item>

      <Form.Item name="skills_group" label="Skills Group">
        <Checkbox.Group options={skillOptions} className="filter-checkbox-group" />
      </Form.Item>

      <Form.Item name="type_format" label="Type/Format">
        <Checkbox.Group options={typeOptions} className="filter-checkbox-group" />
      </Form.Item>

      <Form.Item name="price" label="Max Price (€)">
        <Slider
          className="slider-container"
          min={0}
          max={maxPrice}
          marks={{
            0: '0€',
            [maxPrice]: `${maxPrice}€`,
          }}
        />
      </Form.Item>
    </Form>
  );
};

export default Filter;
